import { PLANTS, type Plant } from '@/data/plants';
import type { PlantingLog } from '@/lib/plantingTracker';

// Guidance guardrail: normalize planting logs before guidance or calendar use.
// Raw legacy logs can display fine while failing canonical plant lookup.
export type PlantingLogRepairConfidence = 'high';

export interface PlantingLogRepairReport {
  logId: string;
  oldPlantId?: string;
  oldPlantName?: string;
  newPlantId: string;
  confidence: PlantingLogRepairConfidence;
  reason: string;
}

export interface PlantingLogUnresolvedReport {
  logId: string;
  plantId?: string;
  plantName?: string;
  reason: string;
}

export interface PlantingLogNormalizationResult {
  logs: PlantingLog[];
  changed: boolean;
  repairs: PlantingLogRepairReport[];
  unresolved: PlantingLogUnresolvedReport[];
}

type LegacyPlantingLog = PlantingLog & {
  name?: unknown;
  displayName?: unknown;
};

function normalizePlantIdentity(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[’']/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function legacyGeneratedIdToName(id: string): string | null {
  const prefix = 'generated-';
  if (!id.startsWith(prefix)) return null;
  return normalizePlantIdentity(id.slice(prefix.length).replace(/-/g, ' '));
}

function getStoredPlantName(log: LegacyPlantingLog): string | undefined {
  if (typeof log.plantName === 'string' && log.plantName.trim()) return log.plantName.trim();
  if (typeof log.name === 'string' && log.name.trim()) return log.name.trim();
  if (typeof log.displayName === 'string' && log.displayName.trim()) return log.displayName.trim();
  return undefined;
}

function buildUniqueNameIndex(plants: Plant[]): Map<string, Plant> {
  const buckets = new Map<string, Plant[]>();
  for (const plant of plants) {
    const key = normalizePlantIdentity(plant.name);
    buckets.set(key, [...(buckets.get(key) ?? []), plant]);
  }

  const unique = new Map<string, Plant>();
  for (const [key, matches] of buckets.entries()) {
    if (matches.length === 1) unique.set(key, matches[0]);
  }
  return unique;
}

function findUnambiguousPlantMatch(
  log: LegacyPlantingLog,
  plantsById: Map<string, Plant>,
  plantsByName: Map<string, Plant>,
): { plant: Plant; reason: string } | null {
  const storedName = getStoredPlantName(log);
  const storedNameKey = storedName ? normalizePlantIdentity(storedName) : null;
  const existingPlantId = typeof log.plantId === 'string' ? log.plantId.trim() : '';

  if (existingPlantId && plantsById.has(existingPlantId)) return null;

  if (storedNameKey) {
    const nameMatch = plantsByName.get(storedNameKey);
    if (nameMatch) {
      return {
        plant: nameMatch,
        reason: existingPlantId
          ? 'stored plant name matches one current catalog plant'
          : 'missing plant id repaired from stored plant name',
      };
    }
  }

  if (existingPlantId) {
    const legacyNameKey = legacyGeneratedIdToName(existingPlantId);
    if (legacyNameKey) {
      const generatedMatch = plantsByName.get(legacyNameKey);
      if (generatedMatch) {
        return {
          plant: generatedMatch,
          reason: 'legacy generated plant id maps to one current catalog plant',
        };
      }
    }
  }

  return null;
}

export function normalizePlantingLogs(
  logs: PlantingLog[],
  plants: Plant[] = PLANTS,
): PlantingLogNormalizationResult {
  const plantsById = new Map(plants.map(plant => [plant.id, plant]));
  const plantsByName = buildUniqueNameIndex(plants);
  const repairs: PlantingLogRepairReport[] = [];
  const unresolved: PlantingLogUnresolvedReport[] = [];

  const normalizedLogs = logs.map(log => {
    const legacyLog = log as LegacyPlantingLog;
    const plantId = typeof legacyLog.plantId === 'string' ? legacyLog.plantId.trim() : '';
    const plantName = getStoredPlantName(legacyLog);

    if (plantId && plantsById.has(plantId)) return log;

    const match = findUnambiguousPlantMatch(legacyLog, plantsById, plantsByName);
    if (!match) {
      unresolved.push({
        logId: log.id,
        plantId: plantId || undefined,
        plantName,
        reason: plantId
          ? 'plant id is not in the current catalog and no unambiguous name match was found'
          : 'missing plant id and no unambiguous name match was found',
      });
      return log;
    }

    repairs.push({
      logId: log.id,
      oldPlantId: plantId || undefined,
      oldPlantName: plantName,
      newPlantId: match.plant.id,
      confidence: 'high',
      reason: match.reason,
    });

    return {
      ...log,
      plantId: match.plant.id,
      plantName: plantName || match.plant.name,
    };
  });

  return {
    logs: normalizedLogs,
    changed: repairs.length > 0,
    repairs,
    unresolved,
  };
}

export function logPlantingLogNormalizationDiagnostics(result: PlantingLogNormalizationResult): void {
  if (process.env.NODE_ENV === 'production') return;
  if (typeof window === 'undefined') return;

  for (const repair of result.repairs) {
    console.info(
      `Planting log normalized: ${repair.logId} repaired from plantName='${repair.oldPlantName ?? ''}' plantId='${repair.oldPlantId ?? ''}' to plantId='${repair.newPlantId}' (${repair.reason})`,
    );
  }

  for (const item of result.unresolved) {
    console.info(
      `Planting log unresolved: ${item.logId} plantName='${item.plantName ?? ''}' plantId='${item.plantId ?? ''}' (${item.reason})`,
    );
  }
}
